// Portal reads the Manager needs for the pickers and for "test as user" (SPEC 8.7, 8.8).
import type { ArcGISIdentityManager } from "@esri/arcgis-rest-request";
import { request } from "@esri/arcgis-rest-request";
import { searchUsers, searchGroups } from "@esri/arcgis-rest-portal";
import type { PortalGroup } from "../policy/identity";

export interface PortalUser { username: string; fullName: string; role: string; }

export class PortalApi {
  private readonly groupCache = new Map<string, Promise<PortalGroup[]>>();
  private readonly groupTitles = new Map<string, string>();

  constructor(private readonly session: ArcGISIdentityManager, private readonly portalUrl: string) {}

  private get rest(): string {
    return `${this.portalUrl.replace(/\/+$/, "")}/sharing/rest`;
  }

  async findUsers(search: string, num = 20): Promise<PortalUser[]> {
    const q = search.trim() === "" ? "*" : `${escapeQ(search)}*`;
    const res = await searchUsers({ q, num, authentication: this.session });
    return (res.results ?? []).map((u: any) => ({ username: u.username, fullName: u.fullName ?? u.username, role: u.role ?? "" }));
  }

  async findGroups(search: string, num = 20): Promise<PortalGroup[]> {
    const q = search.trim() === "" ? "*" : `${escapeQ(search)}*`;
    const res = await searchGroups({ q, num, authentication: this.session });
    const groups = (res.results ?? []).map((g: any) => ({ id: g.id, title: g.title }));
    for (const g of groups) this.groupTitles.set(g.id, g.title);
    return groups;
  }

  async groupTitle(id: string): Promise<string> {
    const cached = this.groupTitles.get(id);
    if (cached) return cached;
    try {
      const g = await request(`${this.rest}/community/groups/${id}`, { authentication: this.session, httpMethod: "GET", params: { f: "json" } });
      const title = g?.title ?? id;
      this.groupTitles.set(id, title);
      return title;
    } catch {
      return id;
    }
  }

  /**
   * The groups a user belongs to, in the order Portal returns them — that order
   * decides which `portalGroups` rule wins (SPEC 5.2), so it must not be sorted.
   * This is the same call the SOI's service account makes, and it needs
   * `portal:admin:viewUsers` for users other than itself.
   */
  async userGroups(username: string): Promise<PortalGroup[]> {
    const key = username.toLowerCase();
    let pending = this.groupCache.get(key);
    if (!pending) {
      pending = request(`${this.rest}/community/users/${encodeURIComponent(username)}`, {
        authentication: this.session,
        httpMethod: "GET",
        params: { f: "json" }
      }).then((u: any) => ((u.groups ?? []) as any[]).map((g) => ({ id: g.id, title: g.title })));
      this.groupCache.set(key, pending);
    }
    return pending;
  }

  async userExists(username: string): Promise<boolean> {
    try {
      const u = await request(`${this.rest}/community/users/${encodeURIComponent(username)}`, {
        authentication: this.session,
        httpMethod: "GET",
        params: { f: "json" }
      });
      return !!u?.username;
    } catch {
      return false;
    }
  }
}

/** Portal search treats these as operators. */
const escapeQ = (s: string): string => s.replace(/[:"()\\]/g, " ").trim();
